import { Controller } from "@hotwired/stimulus"
import { turboFetchForm } from "../util/common"

const buildPopCardMutationObserver = callback => {
  return new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return

        const popcards = node.querySelectorAll(
          '.pop-card-wrapper.standard, .pop-card-wrapper.pop-card-wrapper-common, .pop-card-wrapper-mini.pop-card-wrapper-mini-common'
        )

        if (!popcards.length)
          return

        popcards.forEach(popcard => {
          callback(popcard)
        })
      })
    })
  })
}

//Connects to data-controller="popcard-preview"
export default class extends Controller {
  static targets = ["groupChatField", "ambassadorField", "blur", "change", "preview", "leadToInput", "viewTypeInput"]
  static values = {
    previewUrl: String
  }

  connect() {
    this.updatePopcardPreview = this.updatePopcardPreview.bind(this)

    this.toggleLeadEntityFieldsVisibility()

    this.blurTargets.forEach((target) => {
      target.addEventListener("blur", this.updatePopcardPreview)
    })
    this.changeTargets.forEach((target) => {
      target.addEventListener("change", this.updatePopcardPreview)
    })
    this.element.addEventListener("tags-input:added", this.updatePopcardPreview)
    this.element.addEventListener("tags-input:deleted", this.updatePopcardPreview)

    this.popcardObserver = this.buildPopcardObserver()
    this.popcardObserver.observe(document.body, { childList: true, subtree: true })

    this.injectPopcardScript(200)
  }

  disconnect() {
    this.blurTargets.forEach((target) => {
      target.removeEventListener("blur", this.updatePopcardPreview)
    })
    this.changeTargets.forEach((target) => {
      target.removeEventListener("change", this.updatePopcardPreview)
    })
    this.element.removeEventListener("tags-input:added", this.updatePopcardPreview)
    this.element.removeEventListener("tags-input:deleted", this.updatePopcardPreview)

    this.popcardObserver.disconnect()
    this.cleanupPopcardArtifacts()
  }

  previewUrlValueChanged(newValue, oldValue) {
    if (oldValue && newValue && newValue !== oldValue) {
      this.injectPopcardScript()
    }
  }

  toggleLeadEntityFieldsVisibility() {
    if (!this.hasLeadToInputTarget)
      return

    const leadToValue = this.leadToInputTarget.value

    this.groupChatFieldTarget.classList.add("hidden")
    this.ambassadorFieldTarget.classList.add("hidden")

    if (leadToValue === "group_chat") {
      this.groupChatFieldTarget.classList.remove("hidden")
    } else if (leadToValue === "single_chat") {
      this.ambassadorFieldTarget.classList.remove("hidden")
    }
  }

  updatePopcardPreview() {
    turboFetchForm(this.previewUrlValue, this.element)
      .then(() => this.injectPopcardScript())
  }

  injectPopcardScript(timeout = 100) {
    setTimeout(() => {
      const scriptContent = this.getScriptContent()
      if (!scriptContent)
        return

      this.cleanupPopcardArtifacts()
      this.script = document.createElement('script')
      this.script.id = "popcard-script"
      this.script.textContent = scriptContent
      document.head.appendChild(this.script)
    }, timeout)
  }

  getScriptContent() {
    let script = this.previewTarget?.dataset?.script

    if (!script)
      return null

    const scriptContent = script.replace(/<\/?script[^>]*>/gi, '').trim()
    return scriptContent
  }

  buildPopcardObserver() {
    return buildPopCardMutationObserver(popcard => {
      if (!this.hasPreviewTarget)
        return

      this.resetPopcardPosition(popcard)
      this.previewTarget.appendChild(popcard)
    })
  }

  cleanupPopcardArtifacts() {
    document.querySelectorAll('[class^="pop-card-wrapper"]').forEach(el => el.remove())
    document.querySelectorAll('.pop-card-wrapper-mini').forEach(el => el.remove())
    document.querySelectorAll('body > #tap-pop-card').forEach(el => el.remove())
    document.querySelectorAll('script#mw').forEach(el => el.remove())

    if (window.mw) delete window.mw
    if (window.popCard) delete window.popCard

    if (this.script)
      this.script.remove()
  }

  resetPopcardPosition(popcard) {
    const viewType = this.viewTypeInputTarget.tomselect?.getValue() || 'common'

    Object.assign(popcard.style, {
      position: 'relative',
      top: 'auto',
      right: 'auto',
      bottom: 'auto',
      left: 'auto',
      zIndex: 'auto',
      margin: '0',
      visibility: 'visible',
      marginBottom: '16px'
    })

    const shouldHideMini = viewType === "standard" && popcard.classList.contains('pop-card-wrapper-mini')
    const shouldHideCommon = viewType === "mini" && popcard.classList.contains('pop-card-wrapper-common')

    if (shouldHideMini || shouldHideCommon) {
      popcard.classList.add('hidden')
    }
  }
}
